<div class="justup-dialog-large">
  <vl-header-dialog (close)="dialogRef.close()" [rightElement]="excelBtn">
    {{ 'dashboard_ui.evolution_valeur_title' | trans }}
    <ng-container extras>
      <span class="subheading-2 on-accent">{{ totalValeur() }}</span>
      <!-- Variation de période : masquée en mode date fixe (non pertinente). -->
      @if (deltaText() && !snapshotMode()) {
        <span class="delta-badge"
              [class.delta-positive]="deltaClass() === 'positive'"
              [class.delta-negative]="deltaClass() === 'negative'">
          {{ deltaText() }}
        </span>
      }
      @if (snapshotMode() && refDateDisplay()) {
        <span class="tiny on-accent" style="opacity: 0.85">au {{ refDateDisplay() }}</span>
      }
    </ng-container>
  </vl-header-dialog>

  <ng-template #excelBtn>
    <vl-excel-export-button size="medium"
                            [loading]="exporting()"
                            [disabled]="exporting()"
                            [tooltip]="'dashboard_ui.evolution_valeur_export' | trans"
                            (action)="exportExcel()"/>
  </ng-template>

  <div class="justup-dialog-content flex-col gap-16">
    <!-- Sélecteurs -->
    <div class="flex-row gap-16 wrap align-center">
      <!-- Toggle Période / Date fixe : bascule les contrôles affichés. -->
      <div class="flex-col gap-4">
        <span class="tiny on-background-alt">Analyse</span>
        <vl-chips-toggle [elements]="viewModeElements"
                         [value]="mode()"
                         (valueChange)="setMode($event)"
                         size="small"/>
      </div>
      @if (mode() === 'periode') {
        <div class="flex-col gap-4">
          <span class="tiny on-background-alt">{{ 'dashboard_ui.evolution_valeur_field_range' | trans }}</span>
          <vl-chips-toggle [elements]="rangeElements" [(value)]="range" size="small"/>
        </div>
        <div class="flex-col gap-4">
          <span class="tiny on-background-alt">{{ 'dashboard_ui.evolution_valeur_field_granularity' | trans }}</span>
          <vl-chips-toggle [elements]="granularityElements()" [(value)]="granularity" size="small"/>
        </div>
      }
      <div class="flex-col gap-4">
        <span class="tiny on-background-alt">{{ 'dashboard_ui.evolution_valeur_field_dimension' | trans }}</span>
        <vl-chips-toggle [elements]="dimensionElements" [(value)]="dimension" size="small"/>
      </div>
      @if (dimension() !== 'total' && filterElements().length > 0) {
        <div class="flex-col gap-4">
          <span class="tiny on-background-alt">{{ 'dashboard_ui.evolution_valeur_field_filter' | trans }}</span>
          <vl-multi-checkbox
            [elements]="filterElements()"
            [(value)]="selectedKeys"
            [text]="filterLabel()"
            [showNumber]="true"
            [showTextFilter]="true"/>
        </div>
      }
      <!-- Sélecteurs de date : uniquement en mode DATE FIXE. -->
      @if (mode() === 'dateFixe') {
        <div class="flex-col gap-4">
          <span class="tiny on-background-alt">État du stock à une date</span>
          <div class="flex-row gap-8 align-center ref-date-row">
            <input type="date"
                   class="ref-date-input"
                   [min]="minRefDate() ?? ''"
                   [max]="maxRefDate() ?? ''"
                   [value]="refDate() ?? ''"
                   (change)="onRefDateChange($event)"/>
            @if (refValueLabel(); as label) {
              <span class="ref-date-recap"><strong>{{ label }}</strong></span>
            } @else {
              <span class="tiny on-background-alt">aucune donnée à cette date</span>
            }
          </div>
        </div>

        <div class="flex-col gap-4">
          <span class="tiny on-background-alt">Comparer à une date</span>
          <div class="flex-row gap-8 align-center ref-date-row">
            <input type="date"
                   class="ref-date-input"
                   [min]="minRefDate() ?? ''"
                   [max]="maxRefDate() ?? ''"
                   [value]="compareDate() ?? ''"
                   (change)="onCompareDateChange($event)"/>
            @if (compareDate()) {
              <mat-icon class="ref-date-clear cursor-pointer"
                        matTooltip="Retirer la comparaison"
                        (click)="clearCompareDate()">close</mat-icon>
            }
          </div>
        </div>
      }
      <div class="grow"></div>
      <button type="button"
              class="recompute-btn"
              [disabled]="refreshing()"
              [matTooltip]="'dashboard_ui.evolution_valeur_refresh' | trans"
              (click)="refreshNow()">
        <mat-icon [class.spinning]="refreshing()">sync</mat-icon>
        <span>{{ 'dashboard_ui.evolution_valeur_recompute' | trans }}</span>
      </button>
    </div>

    @if (needsBackfill()) {
      <div class="banner-info flex-row gap-8 align-center">
        <mat-icon>info_outline</mat-icon>
        <span class="grow">{{ 'dashboard_ui.evolution_valeur_partial_warning' | trans }}</span>
        <button type="button"
                class="banner-btn"
                [disabled]="refreshing()"
                (click)="refreshNow()">
          {{ 'dashboard_ui.evolution_valeur_recompute' | trans }}
        </button>
      </div>
    }

    <!-- Chart -->
    <div class="chart-container">
      @if (loading() && !payload()) {
        <ngx-skeleton-loader count="1" [theme]="{'height': '320px', 'background-color': 'var(--color-on-disabled)'}"/>
      } @else if ((payload()?.total?.length ?? 0) > 0) {
        <canvas #chart></canvas>
      } @else {
        <div class="flex-col gap-8 align-center justify-center" style="height: 100%; gap: 8px">
          <mat-icon class="empty-icon">show_chart</mat-icon>
          <span class="caption on-background-alt">{{ 'dashboard_ui.evolution_valeur_empty' | trans }}</span>
          <span class="tiny on-disabled">{{ 'dashboard_ui.evolution_valeur_empty_hint' | trans }}</span>
        </div>
      }
    </div>

    <!-- Bandeau "instantané" : identifie clairement le tableau comme l'état
         des stocks à la date choisie (mode date précise). -->
    @if (snapshotMode()) {
      <div class="snapshot-caption flex-row gap-8 align-center">
        <mat-icon>event_available</mat-icon>
        @if (hasCompare()) {
          <span class="body-2"><strong>Comparaison : {{ refDateDisplay() }} vs {{ compareDateDisplay() }}</strong></span>
          <span class="tiny on-background-alt">« Écart » = état à {{ refDateDisplay() }} − état à {{ compareDateDisplay() }}</span>
        } @else {
          <span class="body-2"><strong>État des stocks au {{ refDateDisplay() }}</strong></span>
          <span class="tiny on-background-alt">valeur par dimension à cette date (et non la valeur actuelle)</span>
        }
      </div>
    }

    <!-- Tableau (uniquement si dimension != total) -->
    @if (dimension() !== 'total' && visibleSeries().length) {
      <div class="dim-table">
        <div class="dim-row dim-header">
          <span class="dim-cell color-cell"></span>
          <span class="dim-cell grow">{{ 'dashboard_ui.evolution_valeur_col_dim' | trans }}</span>
          <span class="dim-cell numeric">
            @if (snapshotMode()) { État au {{ refDateDisplay() }} }
            @else { {{ 'dashboard_ui.evolution_valeur_col_current' | trans }} }
          </span>
          @if (!snapshotMode()) {
            <span class="dim-cell numeric">{{ 'dashboard_ui.evolution_valeur_col_delta' | trans }}</span>
          } @else if (hasCompare()) {
            <span class="dim-cell numeric">Écart vs {{ compareDateDisplay() }}</span>
          }
        </div>
        @for (s of visibleSeries(); track s.dimensionKey) {
          <div class="dim-row"
               [class.dim-row--clickable]="canExpandRow(s.dimensionLabel)"
               (click)="onRowClick(s.dimensionLabel)">
            <span class="dim-cell color-cell">
              @if (canExpandRow(s.dimensionLabel)) {
                <mat-icon class="expand-chevron"
                          [class.expand-chevron--open]="isExpanded(s.dimensionLabel)">chevron_right</mat-icon>
              } @else {
                <span class="color-dot" [style.background-color]="s.color"></span>
              }
            </span>
            <span class="dim-cell grow ellipsis" [matTooltip]="s.dimensionLabel">{{ s.dimensionLabel }}</span>
            <span class="dim-cell numeric">{{ formatVal(currentValue(s.points)) }}</span>
            @if (!snapshotMode()) {
              <!-- Logique PÉRIODE : variation depuis le début de la plage. -->
              @let d = seriesDelta(s.points);
              <span class="dim-cell numeric">
                <span class="delta-inline"
                      [class.delta-positive]="d.cls === 'positive'"
                      [class.delta-negative]="d.cls === 'negative'">
                  {{ d.val >= 0 ? '+' : '−' }}{{ formatVal(d.val < 0 ? -d.val : d.val) }}
                  @if (d.pct !== null) {
                    ({{ d.pct >= 0 ? '+' : '' }}{{ d.pct }}%)
                  }
                </span>
              </span>
            } @else if (hasCompare()) {
              <!-- Logique DATE FIXE : écart entre les 2 dates (A − B). -->
              @let d = snapshotDelta(s.points);
              <span class="dim-cell numeric">
                <span class="delta-inline"
                      [class.delta-positive]="d.cls === 'positive'"
                      [class.delta-negative]="d.cls === 'negative'">
                  {{ d.val >= 0 ? '+' : '−' }}{{ formatVal(d.val < 0 ? -d.val : d.val) }}
                  @if (d.pct !== null) {
                    ({{ d.pct >= 0 ? '+' : '' }}{{ d.pct }}%)
                  }
                </span>
              </span>
            }
          </div>
          @if (canExpandRow(s.dimensionLabel) && isExpanded(s.dimensionLabel)) {
            @for (sub of getSubZones(s.dimensionLabel); track sub.subEntrepot) {
              <!-- Niveau 2 : sous-zone (PDC / NC / expé / Stock direct) -->
              <div class="dim-row dim-row--sub dim-row--clickable"
                   (click)="toggleSubZone(s.dimensionLabel, sub.subEntrepot)">
                <span class="dim-cell color-cell">
                  <mat-icon class="expand-chevron"
                            [class.expand-chevron--open]="isSubZoneExpanded(s.dimensionLabel, sub.subEntrepot)">chevron_right</mat-icon>
                </span>
                <span class="dim-cell grow ellipsis" [matTooltip]="sub.subEntrepot">{{ sub.subEntrepot }}</span>
                <span class="dim-cell numeric">{{ formatVal(sub.total) }}</span>
                <span class="dim-cell numeric on-disabled">—</span>
              </div>
              @if (isSubZoneExpanded(s.dimensionLabel, sub.subEntrepot)) {
                @for (art of sub.articles; track art.article) {
                  <!-- Niveau 3 : article -->
                  <div class="dim-row dim-row--sub2 dim-row--clickable"
                       (click)="toggleArticle(s.dimensionLabel, sub.subEntrepot, art.article)">
                    <span class="dim-cell color-cell">
                      <mat-icon class="expand-chevron"
                                [class.expand-chevron--open]="isArticleExpanded(s.dimensionLabel, sub.subEntrepot, art.article)">chevron_right</mat-icon>
                    </span>
                    <span class="dim-cell grow ellipsis" [matTooltip]="art.article">{{ art.article }}</span>
                    <span class="dim-cell numeric">{{ formatVal(art.total) }}</span>
                    <span class="dim-cell numeric on-disabled">—</span>
                  </div>
                  @if (isArticleExpanded(s.dimensionLabel, sub.subEntrepot, art.article)) {
                    @for (lot of art.lots; track lot.lotNumero) {
                      <!-- Niveau 4 : lot (feuille) -->
                      <div class="dim-row dim-row--sub3">
                        <span class="dim-cell color-cell"></span>
                        <span class="dim-cell grow ellipsis"
                              [matTooltip]="lot.lotNumero + (lot.dluo ? ' · DLUO ' + lot.dluo : '')">
                          <mat-icon class="sub-icon">subdirectory_arrow_right</mat-icon>
                          {{ lot.lotNumero }}
                          <span class="lot-qte">{{ lot.quantite | number:'1.0-2' }} {{ lot.unite }}</span>
                        </span>
                        <span class="dim-cell numeric">{{ formatVal(lot.total) }}</span>
                        <span class="dim-cell numeric on-disabled">—</span>
                      </div>
                    }
                  }
                }
              }
            }
          }
        }
      </div>
    }

    <!-- Note historique partiel -->
    @if (payload()?.availableSince; as since) {
      @if (since > (payload()?.startDate ?? '')) {
        <div class="tiny on-disabled flex-row gap-4 align-center">
          <mat-icon class="info-icon">info_outline</mat-icon>
          <span>{{ 'dashboard_ui.evolution_valeur_partial_history' | trans:{ since: since } }}</span>
        </div>
      }
    }
  </div>
</div>
